// CSV cutting-list exporter — primary saw-operator deliverable.
//
// One row per cut part. Sort order driven by the `sort` option so a shop
// can run the saw "by material, biggest first" (default) or "by cabinet"
// (matches assembly order).

import type { ExportArtifact, ExportContext, Postprocessor } from "./types";

const csvCutList: Postprocessor = {
  id: "csv_cutlist",
  label: "Cutting list (CSV)",
  description: "One row per cut part for the panel saw. Opens in Excel / Google Sheets.",
  category: "cut_list",
  extension: "csv",
  options: [
    {
      key: "sort",
      label: "Sort order",
      type: "select",
      default: "material_size",
      choices: [
        { value: "material_size", label: "By material, biggest first" },
        { value: "cabinet",       label: "By cabinet, assembly order" },
        { value: "part_type",     label: "By part type (sides, doors…)" },
      ],
    },
    {
      key: "include_edges",
      label: "Include edge banding columns",
      type: "boolean",
      default: true,
    },
    {
      key: "units",
      label: "Units",
      type: "select",
      default: "in",
      choices: [
        { value: "in", label: "Inches" },
        { value: "mm", label: "Millimetres" },
      ],
    },
  ],
  generate(ctx, opts): ExportArtifact[] {
    const inToMm = 25.4;
    const conv = opts.units === "mm" ? (n: number) => n * inToMm : (n: number) => n;
    const fmt = opts.units === "mm" ? (n: number) => n.toFixed(1) : (n: number) => n.toFixed(3);

    const rows: Array<Record<string, string>> = [];
    for (const { cabinet, parts } of ctx.parts) {
      for (const p of parts) {
        const material = ctx.catalog.materials.get(p.materialId);
        for (let q = 0; q < (p.qty ?? 1); q++) {
          const row: Record<string, string> = {
            cabinet_id:    String(cabinet._id),
            cabinet_class: cabinet.classId,
            part_id:       p.id,
            part_name:     p.name,
            material:      material?.name ?? `#${p.materialId}`,
            length:        fmt(conv(p.w)),
            width:         fmt(conv(p.h)),
            thickness:     fmt(conv(p.thickness)),
            grain:         p.grain,
            qty:           "1",
          };
          if (opts.include_edges) {
            row.edge_top    = edgeName(ctx, p.edges.t);
            row.edge_bottom = edgeName(ctx, p.edges.b);
            row.edge_left   = edgeName(ctx, p.edges.l);
            row.edge_right  = edgeName(ctx, p.edges.r);
          }
          rows.push(row);
        }
      }
    }

    // Sort
    if (opts.sort === "material_size") {
      rows.sort((a, b) => {
        const m = a.material!.localeCompare(b.material!);
        if (m !== 0) return m;
        return Number(b.length) - Number(a.length);
      });
    } else if (opts.sort === "cabinet") {
      rows.sort((a, b) => Number(a.cabinet_id) - Number(b.cabinet_id));
    } else if (opts.sort === "part_type") {
      rows.sort((a, b) => a.part_id!.localeCompare(b.part_id!));
    }

    if (rows.length === 0) {
      return [{
        filename: `${slug(ctx.project.name)}_cutlist.csv`,
        content: `# No cabinets in project. Add cabinets in the design view first.\n`,
        mimeType: "text/csv",
      }];
    }

    const headers = Object.keys(rows[0]!);
    const lines: string[] = [headers.join(",")];
    for (const r of rows) {
      lines.push(headers.map((h) => esc(r[h] ?? "")).join(","));
    }
    lines.push(""); // trailing newline
    lines.push(`# Project: ${ctx.project.name} · ${ctx.cabinets.length} cabinets · ${rows.length} parts · units=${opts.units}`);

    return [{
      filename: `${slug(ctx.project.name)}_cutlist.csv`,
      content: lines.join("\n"),
      mimeType: "text/csv;charset=utf-8",
    }];
  },
};

function edgeName(ctx: ExportContext, id: number | null): string {
  if (id == null) return "";
  return ctx.catalog.edges.get(id)?.name ?? `#${id}`;
}

function esc(s: string): string {
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function slug(s: string): string {
  return s.replace(/[^\w]+/g, "_").replace(/^_+|_+$/g, "") || "project";
}

export default csvCutList;
